// The pieces every theme page is built from: the swatch strip, the preview,
// the contrast table, the twelve-role editor, and type and corners.
//
// PORTED FROM THE OCTOBER HAND-OFF'S console-themes.jsx, AND THE RULES ARE NOT
// HERE. The roles, the faces, the corners and the audit are xp/ui's
// themeLibrary.ts, which is the module the service's admin/themes.ts enforces
// a save with. So a pair this table marks as failing is a pair the service
// refuses, by the same arithmetic, and neither side can drift from the other.
//
// THE PREVIEW IS PAINTED WITH THE PALETTE AND NOTHING ELSE. Its inline styles
// are the twelve colours being edited, which is data on its way to a visitor's
// screen rather than this desk's own appearance: the desk around it stays on
// the --d-* tokens, and themes.css has no colour in it.

import type { ReactNode } from "react";
import { Seg2, Setting, Tag } from "@nova/desk-ui/parts";
import {
  auditPalette,
  THEME_FACES,
  THEME_RADII,
  THEME_ROLES,
  THEME_SURFACES,
  type ThemePalette,
  type ThemeRadius,
  type ThemeRole,
  type ThemeSurfaceId,
} from "../../../ui/src/themeLibrary.js";

export type ThemeMode = "light" | "dark";

/** What a preview, the editor and a save need of a theme. */
export type ThemeShape = {
  readonly name: string;
  readonly light: ThemePalette;
  readonly dark: ThemePalette;
  readonly headFont: string;
  readonly bodyFont: string;
  readonly radius: ThemeRadius;
};

/**
 * The apps a theme can be pointed at from this console.
 *
 * THEME_SURFACES LESS ENTRY, PHOTO AND BA, which have no app (Gabe, 11 Oct;
 * .cursor/rules/desks.mdc, "Do not build"). The service accepts a pointer for
 * all eight, and a pointer for an app that does not exist is a setting
 * somebody believes they made, which is the sentence admin/themes.ts refuses
 * an unknown key with. When one of the three is built, it comes off this line.
 */
const NOT_BUILT: readonly ThemeSurfaceId[] = ["entry", "photo", "ba"];
export const WEARABLE_SURFACES = THEME_SURFACES.filter(([id]) => !NOT_BUILT.includes(id));

export function surfaceLabel(surface: ThemeSurfaceId): string {
  return THEME_SURFACES.find(([id]) => id === surface)?.[1] ?? surface;
}

/** The roles a swatch strip shows: the ground, the card, the ink, the action and the three statuses. */
const SWATCH_ROLES: readonly ThemeRole[] = ["bg", "card", "ink", "accent", "ok", "warn", "bad"];

export function ThemeSwatches({ p }: { readonly p: ThemePalette }): ReactNode {
  return (
    <span className="tsw" aria-hidden="true">
      {SWATCH_ROLES.map((k) => (
        <span key={k} style={{ background: p[k] }} />
      ))}
    </span>
  );
}

/**
 * What the palette does to a real surface, not a row of chips. Every element
 * is one a visitor meets: a heading, a help line, a field, the action, and the
 * three statuses.
 */
export function ThemePreview({
  t,
  mode,
  label,
}: {
  readonly t: ThemeShape;
  readonly mode: ThemeMode;
  readonly label: string;
}): ReactNode {
  const p = t[mode];
  const r = THEME_RADII[t.radius] ?? THEME_RADII.pill;
  return (
    <figure className="tprev" style={{ background: p.bg, color: p.ink, fontFamily: t.bodyFont, borderRadius: r.box }}>
      <figcaption style={{ color: p.mute }}>{label}</figcaption>
      <div className="tprev-card" style={{ background: p.card, borderColor: p.line, borderRadius: r.box }}>
        <p className="tprev-h" style={{ fontFamily: t.headFont }}>
          Let&rsquo;s make a memory
        </p>
        <p className="tprev-p" style={{ color: p.mute }}>
          Ninety seconds. A photo, a look, and it is on your phone.
        </p>
        <span
          className="tprev-field"
          style={{ background: p.bg, borderColor: p.line, color: p.mute, borderRadius: r.field }}
        >
          you@company.example
        </span>
        <span className="tprev-b" style={{ background: p.accent, color: p.accentInk, borderRadius: r.ctl }}>
          Start
        </span>
      </div>
      <div className="tprev-row">
        <span className="tprev-well" style={{ background: p.media || p.sunk, borderRadius: r.box }} />
        <span className="tprev-tags">
          <i style={{ color: p.ok, borderColor: p.ok }}>Delivered</i>
          <i style={{ color: p.warn, borderColor: p.warn }}>Waiting</i>
          <i style={{ color: p.bad, borderColor: p.bad }}>Refused</i>
        </span>
      </div>
    </figure>
  );
}

/** Both modes side by side, because a visitor's own device decides which one they get. */
export function ThemePreviews({ t }: { readonly t: ThemeShape }): ReactNode {
  return (
    <div className="tprevs">
      <ThemePreview t={t} mode="light" label="Light" />
      <ThemePreview t={t} mode="dark" label="Dark" />
    </div>
  );
}

/**
 * Whether it can be read: every pair the service measures, measured here on
 * every keystroke by the same auditPalette, in the hand-off's own words.
 */
export function ContrastTable({ p }: { readonly p: ThemePalette }): ReactNode {
  return (
    <div className="kv">
      {auditPalette(p).map((r) => (
        <div className="kv-row" key={r.label}>
          <span className="kv-k">{r.label}</span>
          <span className="kv-v">
            <span className={"tag " + (r.ok ? "tag-good" : r.floor >= 3 ? "tag-bad" : "tag-off")}>{r.ratio}:1</span>
            <span className="quiet">
              {" "}
              {r.ok
                ? "passes " + r.floor + ":1"
                : "below " + r.floor + (r.floor >= 3 ? ", refuse it" : ", a hairline is not text")}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

/** The pairs under their floor in one mode: what Save is refused for. */
export function failingPairs(p: ThemePalette): readonly { readonly label: string; readonly floor: number }[] {
  return auditPalette(p).filter((r) => !r.ok && r.floor >= 3);
}

/** The twelve roles, a chip and a hex each. Typed, not picked: contrast is measured on what is typed. */
export function PaletteEditor({
  p,
  onSet,
  locked,
  mode,
}: {
  readonly p: ThemePalette;
  readonly onSet: (role: ThemeRole, value: string) => void;
  readonly locked: boolean;
  readonly mode: ThemeMode;
}): ReactNode {
  return (
    <div className="panel panel-stack">
      {THEME_ROLES.map(([k, label, note]) => (
        <Setting key={k} label={label} info={note}>
          <span className="wsw">
            <span className="wsw-chip" style={{ background: p[k] }} aria-hidden="true" />
            <input
              className="input mono"
              name={`theme-role-${mode}-${k}`}
              value={p[k] ?? ""}
              spellCheck={false}
              autoComplete="off"
              aria-label={label}
              disabled={locked}
              onChange={(event) => onSet(k, event.target.value.trim())}
            />
          </span>
        </Setting>
      ))}
    </div>
  );
}

export function ModeSwitch({
  mode,
  onMode,
}: {
  readonly mode: ThemeMode;
  readonly onMode: (mode: ThemeMode) => void;
}): ReactNode {
  return (
    <Seg2
      label="Mode"
      value={mode === "light" ? "Light" : "Dark"}
      options={["Light", "Dark"] as const}
      onPick={(l) => onMode(l === "Light" ? "light" : "dark")}
    />
  );
}

/**
 * The two faces and the corner. The type scale, the 56px target and the focus
 * ring are not here: they are not for sale.
 */
export function TypeAndCorners({
  t,
  onChange,
  locked,
}: {
  readonly t: ThemeShape;
  readonly onChange: (next: Partial<Pick<ThemeShape, "headFont" | "bodyFont" | "radius">>) => void;
  readonly locked: boolean;
}): ReactNode {
  return (
    <div className="panel panel-stack">
      <Setting label="Heading typeface" info="The line read from across an aisle.">
        <select
          className="input"
          name="theme-head-font"
          value={t.headFont}
          disabled={locked}
          onChange={(event) => onChange({ headFont: event.target.value })}
        >
          {THEME_FACES.map(([v, l]) => (
            <option key={l} value={v}>
              {l}
            </option>
          ))}
        </select>
      </Setting>
      <Setting label="Body typeface" info="Prompts, consents and small print.">
        <select
          className="input"
          name="theme-body-font"
          value={t.bodyFont}
          disabled={locked}
          onChange={(event) => onChange({ bodyFont: event.target.value })}
        >
          {THEME_FACES.map(([v, l]) => (
            <option key={l} value={v}>
              {l}
            </option>
          ))}
        </select>
      </Setting>
      <Setting label="Corner" info="Pills are the Nova brand surfaces; 8px is XP1.">
        <Seg2
          label="Corner"
          value={t.radius === "pill" ? "Pill" : "Square"}
          options={["Pill", "Square"] as const}
          onPick={(l) => {
            if (!locked) onChange({ radius: l === "Pill" ? "pill" : "square" });
          }}
        />
      </Setting>
    </div>
  );
}

/** The house palette's mark on a card: read-only, and why. */
export function ShippedTag(): ReactNode {
  return <Tag off>Shipped</Tag>;
}
